/**
 * C2 Live Occupancy Board — shared bed-state constants (single source of truth).
 *
 * The backend speaks CODES ONLY (contract §1–§3). Every label, colour, dot, and
 * ordering used by the board's tiles, legend, filter chips, mini-bar and totals
 * strip is derived from THIS map — no component re-rolls its own state list or
 * palette (single-source rule; prevents a legend/chip/bar drift like the
 * tooth-chart Done·2-vs-0 bug).
 *
 * Manual (housekeeping) transitions are the ONLY states a MANAGE_BED_STATE user
 * may write from the board — OCCUPIED / RESERVED are owned by the admission
 * workflow (admit / transfer / discharge), never the board.
 */

// Canonical code set + render order (occupied first → highest-signal left).
export const BED_STATES = ['OCCUPIED', 'RESERVED', 'CLEANING', 'BLOCKED', 'VACANT'];

/**
 * Per-state render metadata. `cls` = accessible Tailwind chip classes (solid day
 * tint + dark tint at /40 + matching border, per the transparency/contrast
 * rules); `dot` = the always-present colour dot (label+dot, never colour-only —
 * WCAG). `bar` = the mini-occupancy-bar segment colour (explicit hex so the bar
 * is identical in light + dark). `manual` = a board-writable housekeeping state.
 */
export const BED_STATE_META = {
  OCCUPIED: {
    label: 'Occupied', manual: false,
    cls: 'bg-teal-100 text-teal-900 border-teal-300 dark:bg-teal-900/40 dark:text-teal-100 dark:border-teal-700',
    dot: '#0d9488', bar: '#0d9488',
  },
  RESERVED: {
    label: 'Reserved', manual: false,
    cls: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-700',
    dot: '#f59e0b', bar: '#f59e0b',
  },
  CLEANING: {
    label: 'Cleaning', manual: true,
    cls: 'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-700',
    dot: '#3b82f6', bar: '#3b82f6',
  },
  BLOCKED: {
    label: 'Blocked', manual: true,
    cls: 'bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-900/40 dark:text-rose-100 dark:border-rose-700',
    dot: '#ef4444', bar: '#ef4444',
  },
  VACANT: {
    label: 'Vacant', manual: true,
    cls: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600',
    dot: '#94a3b8', bar: '#cbd5e1',
  },
};

// Legend / state-filter chip set — derived from META so filters can't diverge.
export const BED_STATE_PILLS = BED_STATES.map((code) => ({ code, label: BED_STATE_META[code].label }));

/**
 * S5 manual-transition matrix — for a CURRENT state, the legal board actions.
 * Mirrors the backend legality matrix (contract §2). OCCUPIED / RESERVED → none.
 * `to` = target_state sent to POST /occupancy/beds/{id}/state; `expected` is the
 * current state for the conditional write (optimistic-concurrency guard).
 */
export const MANUAL_TRANSITIONS = {
  VACANT: [
    { to: 'CLEANING', label: 'Mark cleaning' },
    { to: 'BLOCKED', label: 'Block bed' },
  ],
  CLEANING: [
    { to: 'VACANT', label: 'Mark vacant (clean)' },
    { to: 'BLOCKED', label: 'Block bed' },
  ],
  BLOCKED: [
    { to: 'VACANT', label: 'Unblock → vacant' },
    { to: 'CLEANING', label: 'Unblock → cleaning' },
  ],
  OCCUPIED: [],
  RESERVED: [],
};

// Sentinel dept-filter value → whole board, no grouping (strict no-op).
export const ALL_DEPARTMENTS = '';

// Cross-department audience (e.g. Emergency): a filter on it fails OPEN to the
// full board — Emergency staff never lose sight of any ward (rule-(a)).
export const EMERGENCY_DEPT = 'EMERGENCY';
